import React, { useMemo } from "react";
import {
  WidgetInfo,
  WidgetSkeleton,
  fullNumber,
  fullPct,
  num,
  pct,
} from "./skdShared";

export const normalizeProductMix = (data = {}) => {
  const percentages = data.productMixPercentages || {};
  const counts = data.productMixCounts || {};

  return {
    services: num(percentages.services),
    parts: num(percentages.items),
    packages: num(percentages.packages),
    servicesCount: num(counts.services),
    partsCount: num(counts.items),
    packagesCount: num(counts.packages),
    scheduled: num(data.totalPredictions),
    converted: num(data.completedPredictions),
    pending: num(data.incompletePredictions),
    conversionRate: num(data.percentageCompleted),
  };
};

const ProductMixPmsWidget = ({ loading, data, t }) => {
  const mix = useMemo(() => normalizeProductMix(data), [data]);

  const productMixView = useMemo(() => {
    const segments = [
      {
        key: "services",
        label: t("skdServices"),
        value: mix.services,
        color: "#3b82f6",
      },
      {
        key: "items",
        label: t("skdItems"),
        value: mix.parts,
        color: "#f59e0b",
      },
      {
        key: "packages",
        label: t("skdPackages"),
        value: mix.packages,
        color: "#22c55e",
      },
    ];
    const mixTotal =
      segments.reduce((sum, item) => sum + (item.value || 0), 0) || 1;
    const mixSegments = segments.map((item) => ({
      ...item,
      pct: (item.value / mixTotal) * 100,
    }));

    const scheduled = mix.scheduled || mix.converted + mix.pending || 0;
    const converted = mix.converted || 0;
    const pending = mix.pending || Math.max(scheduled - converted, 0);
    const convertedPct = scheduled
      ? (converted / scheduled) * 100
      : mix.conversionRate || 0;
    const pendingPct = scheduled
      ? (pending / scheduled) * 100
      : Math.max(100 - convertedPct, 0);

    return {
      mixSegments,
      scheduled,
      converted,
      pending,
      convertedPct,
      pendingPct,
    };
  }, [mix, t]);

  if (loading) {
    return <WidgetSkeleton height={400} />;
  }

  return (
    <div className="skd-widget skd-widget--product-mix">
      <header className="skd-pmx__head">
        <h3 className="skd-pmx__title">{t("skdProductMixPms")}</h3>
        <WidgetInfo title={t("skdProductMixPmsInfo")} />
      </header>

      <div className="skd-pmx__body">
        <section className="skd-pmx__sec">
          <div className="skd-pmx__label">{t("skdProductMix")}</div>
          <div
            className="skd-pmx__bar"
            role="img"
            aria-label={t("skdProductMix")}
          >
            {productMixView.mixSegments
              .filter((item) => item.value > 0)
              .map((item) => (
                <i
                  key={item.key}
                  title={`${item.label}: ${fullPct(item.pct)}`}
                  style={{
                    width: `${item.pct}%`,
                    background: item.color,
                  }}
                />
              ))}
          </div>
          <ul className="skd-pmx__leg">
            {productMixView.mixSegments.map((item) => (
              <li key={item.key} title={fullPct(item.pct)}>
                <span
                  className="skd-pmx__dot"
                  style={{ background: item.color }}
                />
                <span className="skd-pmx__leg-name">{item.label}</span>
                <b>{pct(item.pct, 1)}</b>
              </li>
            ))}
          </ul>
        </section>

        <section className="skd-pmx__sec skd-pmx__sec--pms">
          <div className="skd-pmx__label">{t("skdPmsConversion")}</div>
          <div className="skd-pmx__big">
            <b title={fullPct(productMixView.convertedPct)}>
              {pct(productMixView.convertedPct, 1)}
            </b>
            <span>
              {t("skdPmsJobsServiced").replace(
                "{{n}}",
                productMixView.scheduled.toLocaleString(),
              )}
            </span>
          </div>
          <div
            className="skd-pmx__cbar"
            role="img"
            aria-label={t("skdPmsConversion")}
          >
            <i
              title={`${t("skdConverted")}: ${fullPct(productMixView.convertedPct)}`}
              style={{
                width: `${productMixView.convertedPct}%`,
                background: "#16a34a",
              }}
            />
            <i
              title={`${t("skdPendingOverdue")}: ${fullPct(productMixView.pendingPct)}`}
              style={{
                width: `${productMixView.pendingPct}%`,
                background: "#f59e0b",
              }}
            />
          </div>
          <div className="skd-pmx__stats">
            <div
              className="skd-pmx__st skd-pmx__st--all"
              title={fullNumber(productMixView.scheduled)}
            >
              <small>{t("skdScheduledPmsJobs")}</small>
              <b>{productMixView.scheduled.toLocaleString()}</b>
            </div>
            <div
              className="skd-pmx__st skd-pmx__st--cv"
              title={`${fullNumber(productMixView.converted)} (${fullPct(productMixView.convertedPct)})`}
            >
              <small>{t("skdConverted")}</small>
              <b>
                {productMixView.converted.toLocaleString()}
                <em>{pct(productMixView.convertedPct, 1)}</em>
              </b>
            </div>
            <div
              className="skd-pmx__st skd-pmx__st--pd"
              title={`${fullNumber(productMixView.pending)} (${fullPct(productMixView.pendingPct)})`}
            >
              <small>{t("skdPendingOverdue")}</small>
              <b>
                {productMixView.pending.toLocaleString()}
                <em>{pct(productMixView.pendingPct, 1)}</em>
              </b>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ProductMixPmsWidget;
